import ProgramInformationModal from '@/components/actions/program/ProgramInformationModal';
import { DataListItem, DataListRoot } from '@/components/ui/data-list';
import { useAuth } from '@/contexts/auth-context';
import { Button, Card, Flex, Link, SimpleGrid, Text, VStack } from '@chakra-ui/react';
import type { Program } from '@fluenceur/types';

import { format } from 'date-fns';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import ProgramDeepLinking from './ProgramDeepLinking';
import ProgramStatus from './ProgramStatus';
import { EditPencil } from 'iconoir-react';

interface AffiliateProgramOverviewDetailsProps {
  program: Program;
}

const AffiliateProgramOverviewDetails = ({ program }: AffiliateProgramOverviewDetailsProps) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [isModalFormOpen, setIsModalFormOpen] = useState(false);

  const destinationUrl = program.destinationUrl || user?.currentBrand?.websiteUrl || null;

  return (
    <>
      <Card.Root w="full" p={8}>
        <Card.Header p={0}>
          <Flex justifyContent="space-between" alignItems="baseline">
            <Card.Title fontSize="2xl">{t('programs.programOverview.details.title')}</Card.Title>
            <Button
              variant="outline"
              w="fit-content"
              size="sm"
              onClick={() => setIsModalFormOpen(true)}
            >
              <EditPencil width={20} height={20} />
              {t('programs.programOverview.details.edit')}
            </Button>
          </Flex>
        </Card.Header>
        <Card.Body p={0} mt={8}>
          <DataListRoot orientation="vertical" gap={6}>
            <SimpleGrid
              columns={[1, null, 3]}
              gap={6}
              templateColumns={{ base: '1fr', md: '1fr 2fr' }}
            >
              <VStack w="full" alignItems="start" gap={6}>
                <DataListItem
                  label={t('programs.programOverview.details.name')}
                  alignItems="baseline"
                  value={
                    <Text fontWeight="medium" fontSize="md">
                      {program.name}
                    </Text>
                  }
                />
                <DataListItem
                  label={t('programs.programOverview.details.programCreated')}
                  value={
                    <Text fontSize="md" fontWeight="medium">
                      {program.createdAt
                        ? format(new Date(program.createdAt), 'dd/MM/yyyy')
                        : 'N/A'}
                    </Text>
                  }
                />
              </VStack>
              <DataListItem
                label={t('programs.programOverview.details.description')}
                lineHeight="moderate"
                alignItems="baseline"
                value={
                  <Text fontSize="15px" fontWeight="medium">
                    {program.description ?? 'N/A'}
                  </Text>
                }
              />
            </SimpleGrid>
            <DataListItem
              label={t('programs.programOverview.details.status')}
              value={<ProgramStatus programId={program.id} isActive={program.enabled} />}
            />
            <DataListItem
              label={t('programs.programOverview.details.destinationUrl')}
              value={
                destinationUrl ? (
                  <Link
                    href={destinationUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    fontSize="md"
                    fontWeight="medium"
                    colorPalette="primary"
                  >
                    {destinationUrl}
                  </Link>
                ) : (
                  <Text fontSize="md" fontWeight="medium">
                    N/A
                  </Text>
                )
              }
            />
            <DataListItem
              label={t('programs.programOverview.settings.deepLinkingEnabled')}
              info={t('programs.programOverview.settings.deepLinkingEnabledDesc')}
              value={
                <ProgramDeepLinking programId={program.id} enabled={program.deepLinkingEnabled} />
              }
            />
          </DataListRoot>
        </Card.Body>
      </Card.Root>
      <ProgramInformationModal
        programId={program.id}
        isOpen={isModalFormOpen}
        onClose={() => setIsModalFormOpen(false)}
        defaultValues={{
          name: program.name,
          description: program.description,
          destinationUrl: program.destinationUrl,
        }}
      />
    </>
  );
};

export default AffiliateProgramOverviewDetails;
